import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Truck, Plus, X, Loader2, Trash2, Edit2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { enterAdvance } from '@/hooks/useEnterAdvance'

interface Veiculo {
  id: number
  placa: string
  uf_licenciamento: string
  renavam: string | null
  tara_kg: number
  capacidade_kg: number
  cubagem_m3: number | null
  tolerancia_peso_pct: number
  tipo_carroceria: string | null
  tipo_rodado: string | null
  proprio: boolean
  ativo: boolean
  observacoes: string | null
}

const TIPO_CARROCERIA = ['aberta', 'fechada', 'granel', 'bau', 'sider', 'outros']
const TIPO_RODADO = ['truck', 'toco', 'cavalo_mecanico', 'van', 'outros']

export default function ExpedicaoVeiculosPage() {
  const qc = useQueryClient()
  const [editar, setEditar] = useState<Veiculo | 'new' | null>(null)

  const { data: veiculos = [], isLoading } = useQuery<Veiculo[]>({
    queryKey: ['veiculos'],
    queryFn: async () => (await api.get('/expedicao/veiculos')).data,
  })

  const inativar = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/expedicao/veiculos/${id}`)).data,
    onSuccess: () => { toast.success('Veículo inativado'); qc.invalidateQueries({ queryKey: ['veiculos'] }) },
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="text-brand-600" /> Veículos
          </h1>
          <p className="text-ink-600 text-sm">Cadastro de veículos próprios e de terceiros pra uso em MDF-e.</p>
        </div>
        <button onClick={() => setEditar('new')} className="btn-primary">
          <Plus size={14} /> Novo veículo
        </button>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Placa</th><th>UF</th><th>RENAVAM</th><th>Tara</th><th>Capacidade</th><th>Cubagem</th>
              <th>Carroceria</th><th>Rodado</th><th>Próprio</th><th className="w-24"></th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={10} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && veiculos.length === 0 && (
              <tr><td colSpan={10} className="text-center py-8 text-ink-500">Nenhum veículo.</td></tr>
            )}
            {veiculos.map(v => (
              <tr key={v.id}
                onClick={() => setEditar(v)}
                className="hover:bg-violet-50 cursor-pointer">
                <td className="font-mono font-semibold">{v.placa}</td>
                <td>{v.uf_licenciamento}</td>
                <td className="text-xs font-mono">{v.renavam || '-'}</td>
                <td className="text-right">{v.tara_kg} kg</td>
                <td className="text-right">{v.capacidade_kg} kg</td>
                <td className="text-right">{v.cubagem_m3 ? `${v.cubagem_m3} m³` : '-'}</td>
                <td className="capitalize text-xs">{v.tipo_carroceria || '-'}</td>
                <td className="capitalize text-xs">{v.tipo_rodado || '-'}</td>
                <td>{v.proprio ? '✓' : '—'}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <button onClick={() => setEditar(v)} className="btn-secondary !py-1 !px-2 text-xs">
                      <Edit2 size={12} />
                    </button>
                    <button onClick={() => pedirExclusao({ descricao: `o veículo ${v.placa}`, onConfirm: () => inativar.mutate(v.id) })}
                      className="btn-secondary !py-1 !px-2 text-xs text-rose-700">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editar && <VeiculoModal
        veiculo={editar === 'new' ? null : editar}
        onClose={() => setEditar(null)}
        onDone={() => { setEditar(null); qc.invalidateQueries({ queryKey: ['veiculos'] }) }}
      />}
    </div>
  )
}

function VeiculoModal({ veiculo, onClose, onDone }: { veiculo: Veiculo | null; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    placa: veiculo?.placa || '',
    uf_licenciamento: veiculo?.uf_licenciamento || 'CE',
    renavam: veiculo?.renavam || '',
    tara_kg: veiculo?.tara_kg || 0,
    capacidade_kg: veiculo?.capacidade_kg || 0,
    cubagem_m3: veiculo?.cubagem_m3 ?? null,
    tolerancia_peso_pct: veiculo?.tolerancia_peso_pct ?? 5,
    tipo_carroceria: veiculo?.tipo_carroceria || 'fechada',
    tipo_rodado: veiculo?.tipo_rodado || 'truck',
    proprio: veiculo?.proprio ?? true,
    observacoes: veiculo?.observacoes || '',
  })
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))

  const salvar = useMutation({
    mutationFn: async () => {
      if (veiculo) return (await api.put(`/expedicao/veiculos/${veiculo.id}`, form)).data
      return (await api.post('/expedicao/veiculos', form)).data
    },
    onSuccess: () => { toast.success(veiculo ? 'Veículo atualizado' : 'Veículo criado'); onDone() },
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onKeyDown={enterAdvance}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">{veiculo ? 'Editar' : 'Novo'} veículo</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Placa *</div>
              <input className="input w-full font-mono uppercase" value={form.placa}
                onChange={e => set('placa', e.target.value.toUpperCase())} maxLength={8} placeholder="ABC1D23" />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">UF *</div>
              <input className="input w-full" value={form.uf_licenciamento} onChange={e => set('uf_licenciamento', e.target.value.toUpperCase())} maxLength={2} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">RENAVAM</div>
              <input className="input w-full font-mono" value={form.renavam} onChange={e => set('renavam', e.target.value)} />
            </label>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Tara (kg)</div>
              <input type="number" className="input w-full" value={form.tara_kg} onChange={e => set('tara_kg', parseInt(e.target.value) || 0)} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Capacidade (kg)</div>
              <input type="number" className="input w-full" value={form.capacidade_kg} onChange={e => set('capacidade_kg', parseInt(e.target.value) || 0)} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Cubagem (m³)</div>
              <input type="number" step="0.01" className="input w-full" value={form.cubagem_m3 ?? ''} onChange={e => set('cubagem_m3', e.target.value === '' ? null : parseFloat(e.target.value))} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1" title="% de peso a mais permitido ao faturar">Tolerância (%)</div>
              <input type="number" step="0.5" className="input w-full" value={form.tolerancia_peso_pct} onChange={e => set('tolerancia_peso_pct', parseFloat(e.target.value) || 0)} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Carroceria</div>
              <select className="input w-full" value={form.tipo_carroceria} onChange={e => set('tipo_carroceria', e.target.value)}>
                {TIPO_CARROCERIA.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Rodado</div>
              <select className="input w-full" value={form.tipo_rodado} onChange={e => set('tipo_rodado', e.target.value)}>
                {TIPO_RODADO.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.proprio} onChange={e => set('proprio', e.target.checked)} />
            Veículo próprio
          </label>
          <label className="block">
            <div className="text-xs font-medium mb-1">Observações</div>
            <textarea className="input w-full" rows={2} value={form.observacoes} onChange={e => set('observacoes', e.target.value)} />
          </label>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button tabIndex={0} disabled={!form.placa || salvar.isPending} onClick={() => salvar.mutate()} className="btn-primary">
            {salvar.isPending && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  )
}
